import React from 'react';
import { useShowKBBPromo } from '@/vdp/StickyBanner/hooks';
import { useDeviceType, useIsNative } from '~/context/common/DeviceContext';
import { useHasOwnerReviews } from '~/context/vdp/GlobalContext';
import { useGetIsWIPVehicle } from '~/context/vdp/VehicleContext';
import { useGetCkPromoFromDiscountContext } from '~/hooks/common/creditKarmaPromo';
import { useGetShowEVTaxTreatment, usePriceDrop } from '~/hooks/vdp';
import useGetShouldRenderBelowSpinner from '~/hooks/vdp/selectors/useGetShouldRenderBelowSpinner';
import useIsNotAvailableForImmediatePurchase from '~/hooks/vdp/selectors/useIsNotAvailableForImmediatePurchase';
import useShouldHideDeliveryPickup from '~/hooks/vdp/selectors/useShouldHideDeliveryPickup';
import useShouldLoadBelowFirstSectionStandard from '~/hooks/vdp/selectors/useShouldLoadBelowFirstSectionStandard';
import useShouldLoadFirstSection from '~/hooks/vdp/selectors/useShouldLoadFirstSection';

function useShowAboutPrice(): boolean {
  const { shouldShowCkPromo } = useGetCkPromoFromDiscountContext();
  const { showPriceDrop } = usePriceDrop();
  const showEVTaxTreatment = useGetShowEVTaxTreatment();
  const showKBBPromo = useShowKBBPromo();

  return (
    shouldShowCkPromo || showPriceDrop || showKBBPromo || showEVTaxTreatment
  );
}

type OwnProps = React.PropsWithChildren<{
  aboutPrice?: boolean;
  deliveryPickup?: boolean;
  desktop?: boolean;
  mobile?: boolean;
  tablet?: boolean;
  native?: boolean;
  notAvailableForImmediatePurchase?: boolean;
  ownerReviews?: boolean;
  renderBelowFirstSection?: boolean;
  renderBelowSpinner?: boolean;
  renderFirstSection?: boolean;
  renderStickyCta?: boolean;
  wip?: boolean;
}>;

export default function ShowConditionally(props: OwnProps) {
  const { isDesktop, isMobile, isTablet } = useDeviceType();
  const hasOwnerReviews = useHasOwnerReviews();
  const isNative = useIsNative();
  const isWIP = useGetIsWIPVehicle();
  const shouldRenderBelowFirstSection =
    useShouldLoadBelowFirstSectionStandard();
  const shouldRenderFirstSection = useShouldLoadFirstSection();
  const isNotAvailableForImmediatePurchase =
    useIsNotAvailableForImmediatePurchase();
  const renderBelowSpinner = useGetShouldRenderBelowSpinner();
  const hideDeliveryPickup = useShouldHideDeliveryPickup();
  const showAboutPrice = useShowAboutPrice();
  const shouldRenderStickCta = !isDesktop;

  const conditions = [
    [props.aboutPrice, showAboutPrice],
    [props.deliveryPickup, !hideDeliveryPickup],
    [props.desktop, isDesktop],
    [props.mobile, isMobile],
    [props.native, isNative],
    [props.tablet, isTablet],
    [
      props.notAvailableForImmediatePurchase,
      isNotAvailableForImmediatePurchase
    ],
    [props.ownerReviews, hasOwnerReviews],
    [props.renderBelowFirstSection, shouldRenderBelowFirstSection],
    [props.renderBelowSpinner, renderBelowSpinner],
    [props.renderFirstSection, shouldRenderFirstSection],
    [props.renderStickyCta, shouldRenderStickCta],
    [props.wip, isWIP]
  ];

  const shouldShow = conditions
    .filter(([condition]) => condition != null)
    .every(([condition, value]) => condition === value);

  return shouldShow ? <>{props.children}</> : null;
}
